import type { Rank } from '../trick/cards.js';
import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat as Seat, SINGLE_PLAYER } from '../bridge/seats.js';
import type { PirateRoundResult } from './round.js';

/** The full ladder up and down: 13 cards down to 1, then 2 back up to 13. The 1-card round is played once. */
export const PIRATE_LADDER: readonly number[] = [
  ...Array.from({ length: 13 }, (_, i) => 13 - i),
  ...Array.from({ length: 12 }, (_, i) => i + 2),
];

/** Big Cards (8 to Ace, 28 cards): 7 down to 1 and back up to 7, 13 rounds. The 1-card round is played once. */
export const BIG_PIRATE_LADDER: readonly number[] = [
  ...Array.from({ length: 7 }, (_, i) => 7 - i),
  ...Array.from({ length: 6 }, (_, i) => i + 2),
];

/** The ladder for a deck whose lowest rank is `minRank`. */
export const pirateLadder = (minRank: Rank = 2): readonly number[] => (minRank >= 8 ? BIG_PIRATE_LADDER : PIRATE_LADDER);

/**
 * `ladder` (the default): play the full ladder once; the highest total wins and ties share the win.
 * `target`: the ladder repeats until someone reaches the target (100 or 200 are the usual choices); if
 * several are past it the highest total wins, and a tie on that total plays one more round.
 */
export type PirateGameMode = { readonly kind: 'ladder' } | { readonly kind: 'target'; readonly target: number };
export const DEFAULT_PIRATE_MODE: PirateGameMode = { kind: 'ladder' };

export interface PirateGame {
  readonly seed: number;
  readonly mode: PirateGameMode;
  /** Deck's lowest rank: 2 full deck, 8 Big Cards. */
  readonly minRank: Rank;
  /** Cap'n's Hat on: each 1-card round is played with the card on the forehead. */
  readonly hat: boolean;
  readonly controllers: BridgeControllers;
  readonly firstDealer: Seat;
  readonly rounds: readonly PirateRoundResult[];
  readonly totals: Readonly<Record<Seat, number>>;
}

export interface NewPirateGameOptions {
  readonly seed: number;
  readonly mode?: PirateGameMode;
  readonly minRank?: Rank;
  readonly hat?: boolean;
  readonly controllers?: BridgeControllers;
  readonly firstDealer?: Seat;
}

export function newPirateGame(opts: NewPirateGameOptions): PirateGame {
  const mode = opts.mode ?? DEFAULT_PIRATE_MODE;
  if (mode.kind === 'target' && !(mode.target > 0)) throw new Error(`target must be positive, not ${mode.target}`);
  return {
    seed: opts.seed,
    mode,
    minRank: opts.minRank ?? 2,
    hat: opts.hat ?? false,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    firstDealer: opts.firstDealer ?? 'N',
    rounds: [],
    totals: { N: 0, E: 0, S: 0, W: 0 },
  };
}

/** What `newPirateRound` needs for the next deal, or null when the game is over. */
export interface PirateRoundSpec {
  readonly index: number;
  readonly seed: number;
  readonly cardsPerHand: number;
  readonly minRank: Rank;
  readonly hat: boolean;
  readonly dealer: Seat;
  readonly controllers: BridgeControllers;
}

/** The dealer rotates every round; each round has its own seed derived from the game's. */
export function nextRoundSpec(game: PirateGame): PirateRoundSpec | null {
  if (pirateWinners(game)) return null;
  const index = game.rounds.length;
  const ladder = pirateLadder(game.minRank);
  const cardsPerHand = ladder[index % ladder.length] as number;
  return {
    index,
    seed: (game.seed ^ Math.imul(index + 1, 0x9e3779b1)) >>> 0,
    cardsPerHand,
    minRank: game.minRank,
    hat: game.hat && cardsPerHand === 1,
    dealer: BRIDGE_SEATS[(BRIDGE_SEATS.indexOf(game.firstDealer) + index) % 4] as Seat,
    controllers: game.controllers,
  };
}

export function recordRound(game: PirateGame, result: PirateRoundResult): PirateGame {
  if (pirateWinners(game)) throw new Error('the game is already over');
  const totals = { ...game.totals };
  for (const s of BRIDGE_SEATS) totals[s] += result[s].score;
  return { ...game, rounds: [...game.rounds, result], totals };
}

/** The winners once the game is over (several when a full ladder ends level), else null. */
export function pirateWinners(game: PirateGame): Seat[] | null {
  const best = Math.max(...BRIDGE_SEATS.map((s) => game.totals[s]));
  const leaders = BRIDGE_SEATS.filter((s) => game.totals[s] === best);
  if (game.mode.kind === 'ladder') return game.rounds.length >= pirateLadder(game.minRank).length ? leaders : null;
  return best >= game.mode.target && leaders.length === 1 ? leaders : null;
}
